import { Submission, Review } from 'src/app/model/paper';
import { ReviewStatus } from 'src/app/enum/review.status';
import { Motives } from 'src/app/enum/assign.motives';

import { UserAssignInfo, SubmissionAssignSuggestions } from './types';
import { AssignmentAlgorithm } from './registry';
import {
  topicsMatchFunction,
  initMaxReviewsPerUser,
  correctOrder,
  shuffleInPlace,
  isEligibleForInterestPhase,
} from './algorithm';

// ============================================================================
// "Fair bid satisfaction" — alocação justa (min-max) pela satisfação dos bids.
// ============================================================================
// Motivação (acordada com o organizador): a satisfação de um membro do TPC = quantos
// dos papers em que ele deu BID (claim, `submission.userClaims`/`hasClaim`) acabaram
// atribuídos a ele. Como cada membro revisa ~o mesmo número de papers (balanceamento
// de carga), o máximo satisfatível de um membro é min(nº de bids, carga-alvo): quem
// deu poucos bids inevitavelmente fica menos satisfeito; quem deu muitos tem mais
// chances — mas deve "liberar" papers disputados para os que deram menos.
//
// Diferença essencial vs. "Highest match first": o clássico percorre as células em
// ordem de PESO GLOBAL (afinidade de tópicos). Aqui percorremos em ordem de CARÊNCIA
// do revisor — sempre atendemos primeiro quem tem menos claims satisfeitos, puxando
// o mínimo para cima (min-max) e evitando o "zero bids".
//
// O `cell.weight` continua sendo a afinidade de tópicos (reuso `topicsMatchFunction`
// com os mesmos fatores +/−) para a UI seguir coerente (tooltip, ordenação do picker,
// getSelectOptions). O que muda é só QUEM é sugerido, não o peso exibido.
//
// Duas fases:
//   A) Claims (gulosa max-min): enquanto houver célula-claim atribuível, escolhe o
//      membro MAIS CARENTE e, entre os claims dele, o paper MAIS DIFÍCIL de preencher.
//   B) Preenchimento por afinidade: garante numberOfReviewsRequired por submissão
//      (idêntico às fases 3-4 do clássico — qualquer elegível, ordem de peso, mMin→mMax),
//      senão papers sem bid ficariam descobertos.

// Slots ainda em aberto numa submissão nesta rodada: revisões requeridas menos as já
// existentes (válidas) mais as já sugeridas. Recalculado a cada checagem porque
// `suggestedAssign` cresce conforme atribuímos.
function remainingSlots(sub: SubmissionAssignSuggestions, numberOfReviewsRequired: number): number {
  const validReviews = sub.submission.reviews.filter(
    r => ![ReviewStatus.DELETED, ReviewStatus.DECLINED].some(s => s === (<Review>r).status));
  return numberOfReviewsRequired - (sub.suggestedAssign.length + validReviews.length);
}

// Efetiva a atribuição de uma célula (contrato idêntico ao do clássico em
// classicAssignmentSuggestion): trava a célula, marca o motivo, credita a carga no
// UserEvent compartilhado e empurra p/ o suggestedAssign da submissão.
function assignCell(cell: UserAssignInfo, motive: string): void {
  cell.elegible = false;
  cell.motive = motive;
  cell.userPreferences.reviewing.push(cell.submission.id);
  cell.submissionToAssign.suggestedAssign.push(cell);
}

// A célula é um "bid vivo": o membro deu claim no paper, a célula ainda é elegível e o
// membro está sob o teto de carga. (O paper ainda precisar de revisor é checado à parte
// via remainingSlots, pois depende do estado corrente.)
function isLiveClaim(cell: UserAssignInfo, maxReviews: number): boolean {
  return cell.elegible
    && cell.userPreferences.reviewing.length < maxReviews
    && cell.submission.hasClaim(cell.userPreferences.user.id);
}

// Quantos membros ainda poderiam cobrir ESTE paper via bid (claim vivo). Menor =
// "mais difícil de preencher" → o membro carente pega esse primeiro e deixa os
// papers disputados (muitos claimers) para os outros. Codifica o "liberar papers".
function claimersRemaining(sub: SubmissionAssignSuggestions, maxReviews: number): number {
  return sub.possiblesAssign.filter(c => isLiveClaim(c, maxReviews)).length;
}

// Fase A — atribuição gulosa max-min por satisfação de bids.
function fairClaimPhase(
  matrix: Array<SubmissionAssignSuggestions>,
  numberOfReviewsRequired: number,
  maxReviews: number
): void {
  // Índice de claims por membro: userId → células-claim (mesma instância de UserEvent
  // em todas). E satisfação corrente por membro, SEMEADA com os claims já atendidos em
  // rodadas anteriores (motive ASSIGNED_CLAIMED, marcado em setEligibility) — assim a
  // justiça é do evento todo, não só desta rodada.
  const claimCellsByUser = new Map<number, Array<UserAssignInfo>>();
  const satisfied = new Map<number, number>();

  matrix.forEach(sub => sub.possiblesAssign.forEach(cell => {
    const uid = cell.userPreferences.user.id;
    if (!satisfied.has(uid)) { satisfied.set(uid, 0); }
    if (cell.motive === Motives.ASSIGNED_CLAIMED) {
      satisfied.set(uid, satisfied.get(uid) + 1);
    }
    if (cell.elegible && cell.submission.hasClaim(uid)) {
      if (!claimCellsByUser.has(uid)) { claimCellsByUser.set(uid, []); }
      claimCellsByUser.get(uid).push(cell);
    }
  }));

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Candidatos = membros com ao menos um bid vivo num paper que ainda precisa de
    // revisor, e sob o teto de carga.
    const candidates: Array<{ uid: number; member: UserAssignInfo['userPreferences']; available: Array<UserAssignInfo>; satisfied: number }> = [];
    claimCellsByUser.forEach((cells, uid) => {
      const member = cells[0].userPreferences;
      if (member.reviewing.length >= maxReviews) { return; }
      const available = cells.filter(c => isLiveClaim(c, maxReviews) && remainingSlots(c.submissionToAssign, numberOfReviewsRequired) > 0);
      if (available.length === 0) { return; }
      candidates.push({ uid, member, available, satisfied: satisfied.get(uid) || 0 });
    });
    if (candidates.length === 0) { break; }

    // Membro MAIS CARENTE (chave asc): 1) menos claims satisfeitos; 2) menos
    // oportunidades restantes (protege quem pode ficar em zero — o high-bidder espera);
    // 3) menor carga total; 4) aleatório (shuffle antes do sort estável).
    shuffleInPlace(candidates);
    candidates.sort((a, b) =>
      (a.satisfied - b.satisfied)
      || (a.available.length - b.available.length)
      || (a.member.reviewing.length - b.member.reviewing.length));
    const chosen = candidates[0];

    // Entre os bids do membro, o paper MAIS DIFÍCIL de preencher (menos claimers
    // vivos); desempate por maior afinidade de tópicos; depois aleatório.
    const cellChoices = chosen.available.slice();
    shuffleInPlace(cellChoices);
    cellChoices.sort((a, b) =>
      (claimersRemaining(a.submissionToAssign, maxReviews) - claimersRemaining(b.submissionToAssign, maxReviews))
      || (b.weight - a.weight));
    const cell = cellChoices[0];

    assignCell(cell, Motives.CLAIM);
    satisfied.set(chosen.uid, chosen.satisfied + 1);
  }
}

// Fase B — preenchimento por afinidade de tópicos. Idêntico às fases 3-4 do clássico:
// qualquer elegível, na ordem de peso (desc), com teto mMin e depois mMax. Garante que
// TODA submissão alcance numberOfReviewsRequired mesmo sem bids suficientes.
function interestFillPhase(
  matrix: Array<SubmissionAssignSuggestions>,
  numberOfReviewsRequired: number,
  mMin: number,
  mMax: number
): void {
  const flat: Array<UserAssignInfo> = [];
  matrix.forEach(sub => flat.push(...sub.possiblesAssign));

  // Desempate aleatório antes do sort estável por peso (mesma técnica do clássico).
  shuffleInPlace(flat);
  flat.sort((a, b) => b.weight - a.weight);

  [mMin, mMax].forEach(max => {
    flat.forEach(cell => {
      if (cell.elegible
        && remainingSlots(cell.submissionToAssign, numberOfReviewsRequired) > 0
        && isEligibleForInterestPhase(cell.submission, cell.userPreferences, max)) {
        assignCell(cell, Motives.INTEREST);
      }
    });
  });
}

/**
 * "Fair bid satisfaction" — alocação justa (min-max) pela satisfação dos bids (claims).
 * Peso da célula = afinidade de tópicos (p/ a UI); a atribuição prioriza o membro com
 * menos claims satisfeitos. Reusa os mesmos fatores +/− do clássico (nenhum parâmetro
 * novo por ora).
 */
export const fairBidSatisfaction: AssignmentAlgorithm = {
  id: 'fair-bid-satisfaction',
  labelKey: 'admin.event.reviews.assign-suggestion.algorithms.fair-bid-satisfaction',
  descriptionKey: 'admin.event.reviews.assign-suggestion.algorithm-descriptions.fair-bid-satisfaction',
  params: [
    { name: 'positiveMatchWeight', labelKey: 'admin.event.reviews.assign-suggestion.positive-match-weight', default: 1 },
    { name: 'negativeMatchWeight', labelKey: 'admin.event.reviews.assign-suggestion.negative-match-weight', default: 1 },
  ],
  run(matrix, ctx, params) {
    // Peso da célula = afinidade de tópicos (mantém a UI coerente; NÃO dirige a escolha).
    matrix.forEach(sub => sub.possiblesAssign.forEach(cell => {
      cell.weight = topicsMatchFunction(
        cell.userPreferences, cell.submission,
        params.positiveMatchWeight, params.negativeMatchWeight);
    }));

    const [mMin, mMax] = initMaxReviewsPerUser(matrix.length, ctx.numberOfCommittees, ctx.numberOfReviewsRequired);

    // A) satisfação justa dos bids; B) preenche o resto por afinidade.
    fairClaimPhase(matrix, ctx.numberOfReviewsRequired, mMax);
    interestFillPhase(matrix, ctx.numberOfReviewsRequired, mMin, mMax);

    return correctOrder(matrix);
  },
};
